'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["messageId", "index", "items", "formatDate", "slots", "slotProps"];
import * as React from 'react';
import useSlotProps from '@mui/utils/useSlotProps';
import { useMessage, useMessageIds } from "../hooks/useMessage.js";
import { useIsHydrated } from "../chat/internals/useIsHydrated.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function resolveMessageIndex(messageId, index, items) {
  if (index != null) {
    return index;
  }
  return items.indexOf(messageId);
}
function parseDate(value) {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
function formatIsoDay(date) {
  return date.toISOString().slice(0, 10);
}
function formatLocalDate(date) {
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}
function isSameCalendarDay(left, right) {
  return formatIsoDay(left) === formatIsoDay(right);
}
export const MessageListDateDivider = /*#__PURE__*/React.forwardRef(function MessageListDateDivider(props, ref) {
  const {
      messageId,
      index,
      items: itemsProp,
      formatDate,
      slots,
      slotProps
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const defaultItems = useMessageIds();
  const items = itemsProp ?? defaultItems;
  const messageIndex = resolveMessageIndex(messageId, index, items);
  const previousMessageId = messageIndex > 0 ? items[messageIndex - 1] : undefined;
  const message = useMessage(messageId);
  const previousMessage = useMessage(previousMessageId ?? '');
  const currentDate = parseDate(message?.createdAt);
  const previousDate = parseDate(previousMessage?.createdAt);
  const hasBoundary = messageIndex > 0 && currentDate != null && previousDate != null && !isSameCalendarDay(previousDate, currentDate);
  const isHydrated = useIsHydrated();
  const label = isHydrated && currentDate ? formatDate?.(currentDate) ?? formatLocalDate(currentDate) : null;
  const ownerState = React.useMemo(() => ({
    messageId,
    hasBoundary,
    label
  }), [hasBoundary, label, messageId]);
  const Divider = slots?.divider ?? 'div';
  const Line = slots?.line ?? 'div';
  const Label = slots?.label ?? 'div';
  const dividerProps = useSlotProps({
    elementType: Divider,
    externalSlotProps: slotProps?.divider,
    externalForwardedProps: other,
    ownerState,
    additionalProps: {
      ref,
      role: 'separator'
    }
  });
  const lineProps = useSlotProps({
    elementType: Line,
    externalSlotProps: slotProps?.line,
    ownerState
  });
  const labelProps = useSlotProps({
    elementType: Label,
    externalSlotProps: slotProps?.label,
    ownerState
  });
  if (!hasBoundary) {
    return null;
  }
  return /*#__PURE__*/_jsxs(Divider, _extends({}, dividerProps, {
    children: [/*#__PURE__*/_jsx(Line, _extends({}, lineProps)), /*#__PURE__*/_jsx(Label, _extends({}, labelProps, {
      children: label
    })), /*#__PURE__*/_jsx(Line, _extends({}, lineProps))]
  }));
});
if (process.env.NODE_ENV !== "production") MessageListDateDivider.displayName = "MessageListDateDivider";